import { useEffect, useMemo, useRef, useState } from "react";
import "@mediapipe/selfie_segmentation";
import { LivechatSubjectType } from "@scrile/api-provider/dist/api/LivechatsProvider";
import { Subject } from "@scrile/api-provider/dist/api/SubjectProvider";
import useConsumeController from "hooks/useConsumeController";
import useLiveChat from "hooks/useLiveChat";
import useProduceController from "hooks/useProduceController";
import useStreamHelper from "hooks/useStreamHelper";
import { ProduceUser } from "types";

export default function useProduceConsumeController(
  livechatSubject: Subject<LivechatSubjectType>,
  autoProduceStream: boolean
) {
  const { loading, token, joinData, endChat } = useLiveChat({
    livechatSubject,
  });

  const {
    stream: produceStream,
    screenStream: produceScreenStream,
    mediaConstraints: produceMediaConstraints,
    connection: produceConnection,
    closeStream: closeProduceStream,
    onChangeConstraints,
    onProduceStream,
    audioInputs,
    videoInputs,
    processing,
    loading: loadingProduceData,
    onProducerFinishedStream,
    producerSpeaking,
    permissions,
  } = useProduceController({ token, joinData, autoProduceStream, audioAnalyzer: true });

  const { closeStream: closeConsumeStream, users, isProducerFinishedStream } = useConsumeController({
    joinData,
    token,
  });

  const { checkStreamForChanges } = useStreamHelper();

  const localProduceStream = useRef<MediaStream>(produceStream);
  const localProduceScreenStream = useRef<MediaStream>(produceScreenStream);
  const usersToView = useMemo(() => Object.values(users), [users]);
  const [me, setMe] = useState<ProduceUser | null>(null);

  useEffect(() => {
    if (!produceStream || !localProduceStream.current) return;
    checkStreamForChanges(produceStream, localProduceStream.current);
  }, [produceStream, checkStreamForChanges]);

  useEffect(() => {
    if (!produceScreenStream || !localProduceScreenStream.current) return;
    checkStreamForChanges(produceScreenStream, localProduceScreenStream.current);
  }, [produceScreenStream, checkStreamForChanges]);

  useEffect(() => {
    setMe(
      joinData
        ? {
            user: joinData.me,
            stream: localProduceStream.current,
            screenStream: localProduceScreenStream.current,
            constrains: produceMediaConstraints,
            isSpeaking: producerSpeaking,
          }
        : null
    );
  }, [joinData, produceMediaConstraints, localProduceStream, localProduceScreenStream, producerSpeaking]);

  useEffect(() => {
    window.addEventListener("beforeunload", endChat);
    return () => {
      window.removeEventListener("beforeunload", endChat);
    };
  }, [endChat]);

  const onCloseStream = () => {
    closeProduceStream();
    closeConsumeStream();
    endChat();
  };

  return {
    threadId: joinData?.threadId,
    users: usersToView,
    userList: joinData?.users ?? [],
    me,
    produceMediaConstraints,
    onProduceStream,
    onCloseStream,
    onChangeConstraints,
    loading: loading || loadingProduceData || processing,
    token,
    audioInputs,
    videoInputs,
    produceConnection,
    isProducerFinishedStream,
    onProducerFinishedStream,
    permissions,
  };
}
